/* eslint-disable react-hooks/exhaustive-deps */
/* PACKAGES */
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Button, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useFormik } from "formik";
import * as yup from "yup";

/* COMPONENTS */
import Topbar from "../../PulseSurvey/Topbar";
import CustomCheckBox from "../../../component/common/CustomCheckBox";
import Loader from "../../../component/common/Loader";

/* ICONS */
import { RiLink } from "react-icons/ri";
import { HiOutlineTrash } from "react-icons/hi2";
import { GoPlus } from "react-icons/go";
import { useParams } from "react-router-dom";

/* CONFIGS */
import { Api } from "../../../api";
import { errorToast, successToast, uploadImage } from "../../../utils/helper";
import { B2BWellPointsViewInvoicePageData } from "../../../utils/pageContent";

/* VALIDATIONS */
const validationSchema = yup.object().shape({
  invoiceNumber: yup.string().required(B2BWellPointsViewInvoicePageData.invoiceNumberRequiredMessage),
  invoiceAmount: yup.string().required(B2BWellPointsViewInvoicePageData.invoiceAmountRequriedMessage),
  file: yup.mixed().required(B2BWellPointsViewInvoicePageData.fileRequriedMessage),
  status: yup.string().required(B2BWellPointsViewInvoicePageData.statusRequriedMessage),
});

/* FILE SIZE LIMIT - 30MB in bytes */
const MAX_FILE_SIZE = 30 * 1024 * 1024;

const GenerateInvoice = () => {
  /* LOCATION */
  const location = useParams();

  /* INVOICE ID */
  const invoiceId = location.id;

  /* INPUT REF */
  const fileInputRef = useRef(null);

  /* CHECKBOX STATE */
  const [emailCheckbox, setEmailCheckbox] = useState(false);

  /* INVOICE DETAILS */
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const pageData = B2BWellPointsViewInvoicePageData;

  /* FORM HANDLER */
  const formHandler = useFormik({
    initialValues: {
      invoiceNumber: "",
      invoiceAmount: "",
      file: "",
      status: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("invoice_number", values.invoiceNumber);
      formData.append("invoice_amount", values.invoiceAmount);
      formData.append("file", selectedFile);
      formData.append("status", values.status);
      formData.append("id", invoiceId);

      formData.forEach((value, key) => {
        console.log(key, value);
      });

      setIsLoadingUpdate(true);
      Api.updateWellpointsCustomHistory(formData)
        .then(async (response) => {
          if (response?.data?.meta?.code === 200) {
            const meta = response?.data?.meta;
            if (meta?.filename && meta?.uploadURL) {
              const uploadImageResponse = await uploadImage(
                selectedFile,
                meta?.uploadURL,
                meta?.filename
              );
              if (uploadImageResponse?.status === 200) {
                setIsLoadingUpdate(false);
                fetchInvoiceDetails();
                successToast(pageData.imageUploadedSuccessMessage);
              }
            } else {
              setIsLoadingUpdate(false);
            }
            successToast(response?.data?.meta?.message);
          } else {
            setIsLoadingUpdate(false);
            errorToast(response?.data?.meta?.message);
          }
        })
        .catch((error) => {
          setIsLoadingUpdate(false);
          errorToast(error);
        });
    },
  });

  const handleChangeStatus = () => {
    const obj = {
      status: 1,
      id: invoiceId,
    };
    Api.updateWellpointsHistoryStatus(obj)
      .then((res) => {
        fetchInvoiceDetails();
        successToast(pageData.statusUpdatedSuccessMessage);
        setConfirmationDialog(!confirmationDialog);
      })
      .catch((err) => {
        console.log("Error: ", err);
      });
  };

  const handleChangeEmailStatus = () => {
    const obj = {
      status: !emailCheckbox,
      id: invoiceId,
    };
    Api.updateWellpointsHistoryEmailStatus(obj)
      .then((res) => {
        fetchInvoiceDetails();
        successToast(pageData.statusUpdatedSuccessMessage);
      })
      .catch((err) => {
        console.log("Error: ", err);
      });
  };

  /* FORMAT FILE SIZE */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  /* FILE SELECT */
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        console.log("File size exceeds limit");
        errorToast(pageData.fileSizeLessThan30MBMessage);
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      const fileSize = formatFileSize(file.size);
      formHandler.setFieldValue("file", {
        file: file,
        fileType: file.type,
        fileName: file.name,
        extension: file.name.split(".").pop(),
        fileSize: fileSize,
        link: URL.createObjectURL(file),
      });
      setSelectedFile(file);
    }
  };

  /* HANDLE FILE CLICK */
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  /* HANDLE FILE DELETE */
  const handleFileDelete = () => {
    if (formHandler.values["file"]?.link) {
      URL.revokeObjectURL(formHandler.values["file"].link);
    }
    formHandler.setFieldValue("file", null);
    setSelectedFile(null);
  };

  /* FETCH INVOICE DETAILS */
  const fetchInvoiceDetails = async () => {
    setLoading(true);
    Api.getWellPointTransactionDataById(invoiceId)
      .then((res) => {
        setInvoiceData(res?.data?.data);
        formHandler.setFieldValue(
          "invoiceNumber",
          res?.data?.data?.invoice_number || ""
        );
        formHandler.setFieldValue(
          "invoiceAmount",
          res?.data?.data?.invoice_amount || ""
        );
        formHandler.setFieldValue("status", res?.data?.data?.status);
        setEmailCheckbox(res?.data?.data?.email_sent);
      })
      .catch((err) => {
        console.log("Error: ", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* FILEDATA */
  const fileData = formHandler.values["file"];

  /* USE EFFECT */
  useEffect(() => {
    fetchInvoiceDetails();
  }, [invoiceId]);

  return (
    <div className="relative pl-5">
      {loading && <Loader />}

      {/* HELMET */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageData.metaTitle}</title>
      </Helmet>

      {/* TOPBAR */}
      <Topbar
        title={pageData.pageTitle}
        hasDesc
        description={pageData.pageDescription}
      />

      {/* DETAILS */}
      <div className="mt-10">
        {/* FIRST SECTION */}
        <form
          onSubmit={formHandler.handleSubmit}
          className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg"
        >
          <div className="p-5">
            <div className="grid grid-cols-2 gap-14">
              {/* LEFT SECTION */}
              <div className="col-span-1">
                {/* COMPANY DETAILS */}
                <div className="mt-0">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      {pageData.companyDetailsTitle}
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      {pageData.companyDetailsDescription}
                    </p>
                  </div>

                  <div className="mt-5">
                    {/* NAME */}
                    <div className="mt-0">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.companyNameLabel}
                        </label>
                      </div>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="company"
                          value={invoiceData?.user_id?.company_id?.company_name}
                          disabled
                          placeholder={pageData.companyNamePlaceholder}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.company &&
                            formHandler.errors.company
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>

                    {/* EMAIL */}
                    <div className="mt-5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.emailLabel}
                        </label>
                      </div>
                      <div className="mt-1">
                        <input
                          type="email"
                          name="companyEmail"
                          value={
                            invoiceData?.user_id?.company_id?.company_email
                          }
                          disabled
                          placeholder={pageData.companyEmailPlaceholder}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.companyEmail &&
                            formHandler.errors.companyEmail
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* INVOICE DETAILS */}
                <div className="mt-10">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      {pageData.invoiceDetailsTitle}
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      {pageData.invoiceDetailsDescription}
                    </p>
                  </div>

                  <div className="mt-5">
                    {/* NAME */}
                    <div className="mt-0">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.nameLabel}
                        </label>
                      </div>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="name"
                          value={invoiceData?.invoiceDetail?.name}
                          disabled
                          placeholder={pageData.namePlaceholder}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.name && formHandler.errors.name
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>

                    {/* EMAIL */}
                    <div className="mt-5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.emailLabel}
                        </label>
                      </div>
                      <div className="mt-1">
                        <input
                          type="email"
                          name="email"
                          value={invoiceData?.invoiceDetail?.email}
                          disabled
                          placeholder={pageData.emailPlaceholder}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.email &&
                            formHandler.errors.email
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>

                    {/* ADDRESS */}
                    <div className="mt-5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.addressLabel}
                        </label>
                      </div>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="address"
                          value={
                            invoiceData?.invoiceDetail?.address +
                            ", " +
                            invoiceData?.invoiceDetail?.postalCode
                          }
                          disabled
                          placeholder={pageData.addressPlaceholder}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.address &&
                            formHandler.errors.address
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SECTION */}
              <div className="col-span-1">
                {/* ATTACHED INVOICE/PREVIEW */}
                <div className="mt-0">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      {pageData.attachedInvoiceOrPreviewTitle}
                    </h5>
                  </div>

                  {/* FILE BOX */}
                  {!invoiceData?.invoice && invoiceData?.status !== 1 && (
                    <div className="mt-5 bg-[#F4F4F4] dark:bg-shoorah-darkBgColor rounded-lg p-5">
                      <p className="text-sm font-normal text-black dark:text-white">
                        {pageData.attachedInvoiceOrPreviewDescription}
                      </p>

                      {/* FILE */}
                      <div className="mt-5">
                        {/* FILE INPUT */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          className="hidden"
                          accept="*/*"
                          onInvalid={(e) =>
                            errorToast(pageData.fileSizeLessThan5MBMessage)
                          }

                        />

                        {/* FILE UPLOAD OPTION */}
                        {(fileData === null ||
                          fileData === "" ||
                          fileData === undefined) && (
                            <div
                              className="group flex items-center gap-3 cursor-pointer"
                              onClick={handleFileClick}
                            >
                              <div className="w-8 h-8 bg-white dark:bg-shoorah-darkBgTabColor flex items-center justify-center rounded-md">
                                <GoPlus className="w-5 h-5" />
                              </div>
                              <p className="text-sm font-medium text-black dark:text-white group-hover:text-shoorah-primary">
                                {pageData.addNewFileBtnText}
                              </p>
                            </div>
                          )}

                        {/* UPLOADED FILE */}
                        {fileData && (
                          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg py-3 px-4">
                            <div className="flex items-center justify-between">
                              <div className="p-0">
                                <div className="flex items-center gap-4">
                                  <RiLink className="w-5 h-5 text-black dark:text-white flex-shrink-0" />
                                  <div className="p-0">
                                    <h5 className="text-sm font-medium text-black dark:text-white">
                                      {fileData.fileName}
                                    </h5>
                                    <p className="mt-1 text-xs font-normal text-black/80 dark:text-white/80">
                                      {pageData.fileSizeText} {fileData.fileSize}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <Button
                                onClick={handleFileDelete}
                                className="bg-[#E35949] hover:bg-opacity-80 transition-all duration-300 ease-in-out rounded-md p-2"
                              >
                                <HiOutlineTrash className="w-5 h-5 text-white" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* OPTIONS */}
                      {fileData && (
                        <div className="mt-5 flex items-center gap-2">
                          <a
                            href={fileData.link}
                            download={fileData.fileName}
                            rel="noreferrer"
                            target="_blank"
                            className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out"
                          >
                            {pageData.downloadFileBtnText}
                          </a>
                          <p className="text-sm font-normal text-black dark:text-white">
                            |
                          </p>
                          <p
                            onClick={handleFileClick}
                            className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out cursor-pointer"
                          >
                            {pageData.replaceFileBtnText}
                          </p>
                        </div>
                      )}

                      {/* VALIDATION */}
                      {formHandler.touched.file && formHandler.errors.file && (
                        <p className="mt-4 text-xs font-normal text-red-400">
                          {pageData.pleaseAttachedInvoiceMessage}
                        </p>
                      )}
                    </div>
                  )}

                  {invoiceData?.invoice && (
                    <div className="mt-5 bg-[#F4F4F4] dark:bg-shoorah-darkBgColor rounded-lg p-5">
                      <p className="text-sm font-normal text-black dark:text-white">
                        {pageData.attachedInvoiceOrPreviewDescription}
                      </p>

                      {/* FILE */}
                      <div className="mt-5">
                        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg py-3 px-4">
                          <div className="flex items-center gap-4">
                            <RiLink className="w-5 h-5 text-black dark:text-white flex-shrink-0" />
                            <div className="p-0">
                              <h5 className="text-sm font-medium text-black dark:text-white">
                                {invoiceData?.invoice?.split("/")[5] || "-"}
                              </h5>
                              {/* <p className="mt-1 text-xs font-normal text-black/80 dark:text-white/80">
                              File size: {invoiceDetails.file.fileSize}
                            </p> */}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* OPTIONS */}
                      <div className="mt-5 flex items-center gap-2">
                        <a
                          href={invoiceData?.invoice}
                          rel="noreferrer"
                          target="_blank"
                          className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out"
                        >
                          {pageData.downloadFileBtnText}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* ADDITIONAL DETAILS */}
                <div className="mt-10">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      {pageData.additionalDetailsText}
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      {pageData.additionalDetailsDescription}
                    </p>
                  </div>

                  <div className="mt-5">
                    {/* INVOICE NUMBER */}
                    <div className="mt-0">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.invoiceNumberLabel}
                        </label>
                        {formHandler.touched.invoiceNumber &&
                          formHandler.errors.invoiceNumber && (
                            <p className="text-xs font-normal text-red-400">
                              {formHandler.errors.invoiceNumber}
                            </p>
                          )}
                      </div>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="invoiceNumber"
                          value={formHandler.values.invoiceNumber}
                          onChange={formHandler.handleChange}
                          placeholder={pageData.invoiceNumberPlaceholder}
                          disabled={invoiceData?.status === 1}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.invoiceNumber &&
                            formHandler.errors.invoiceNumber
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>

                    {/* AMOUNT */}
                    <div className="mt-5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-normal text-black dark:text-white">
                          {pageData.amountLabel}
                        </label>
                        {formHandler.touched.invoiceAmount &&
                          formHandler.errors.invoiceAmount && (
                            <p className="text-xs font-normal text-red-400">
                              {formHandler.errors.invoiceAmount}
                            </p>
                          )}
                      </div>

                      <div className="mt-1">
                        <input
                          type="text"
                          name="invoiceAmount"
                          value={formHandler.values.invoiceAmount}
                          onChange={formHandler.handleChange}
                          placeholder={pageData.amountPlaceholder}
                          disabled={invoiceData?.status === 1}
                          className={`w-full block disabled:bg-[#F4F4F4] dark:bg-white/5 rounded-lg px-4 py-3 text-black bg-white border ${formHandler.touched.invoiceAmount &&
                            formHandler.errors.invoiceAmount
                            ? "border-red-400"
                            : "border-[#E7E8EA] dark:border-white/10"
                            }  text-sm`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {invoiceData?.status !== 1 && (
              <div className="flex items-center justify-end">
                <Button
                  type="submit"
                  disabled={isLoadingUpdate}
                  isLoading={isLoadingUpdate}
                  loadingText="Saving..."
                  className="mt-5 bg-shoorah-primary disabled:bg-[#F4F4F4] disabled:border-[#EAEAEA] border border-shoorah-primary rounded-full text-sm font-medium text-white disabled:text-black/70 px-5 py-2"
                >
                  {pageData.saveDetailsBtnText}
                </Button>
              </div>
            )}
          </div>
        </form>

        {/* SECOND SECTION */}
        <div className="mt-5">
          <div className="grid grid-cols-2 gap-5">
            {/* WELLPOINTS INFORMATION */}
            <div className="col-span-1">
              <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
                <div className="p-5">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      {pageData.wellPointsLabel}
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      {pageData.wellPointsDescription}
                    </p>
                  </div>

                  {/* CONTENT */}
                  <div className="mt-5">
                    <div className="w-full border border-[#E7E8EA] dark:border-white/10 rounded-lg px-4 py-3">
                      <p className="text-sm font-normal text-black dark:text-white">
                        <span className="font-medium">{pageData.totalrequstText} </span>{" "}
                        {invoiceData?.well_points} {pageData.wellPointsText}
                      </p>
                    </div>
                    <div className="mt-5 w-full border border-[#E7E8EA] dark:border-white/10 rounded-lg px-4 py-3">
                      <p className="text-sm font-normal text-black dark:text-white">
                        <span className="font-medium">{pageData.totalAmountText} </span> {pageData.currencySign}
                        {parseFloat(invoiceData?.wellpoints_amount).toFixed(2)}
                      </p>
                    </div>
                    {/* <div className="mt-5">
                      <Button className="bg-shoorah-primary disabled:bg-[#F4F4F4] disabled:border-[#EAEAEA] border border-shoorah-primary rounded-full text-sm font-medium text-white disabled:text-black/70 px-5 py-2">
                        Release Wellpoints
                      </Button>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>

            {/* INTERNAL STATUS */}
            <div className="col-span-1">
              <div className="h-full bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
                <div className="p-5">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      {pageData.internalStatusLabel}
                    </h5>
                    <p className="text-sm font-normal text-black/80 dark:text-white/80">
                      {pageData.internalStatusDescription}
                    </p>
                  </div>

                  {/* CONTENT */}
                  <div className="mt-5">
                    {invoiceData?.status === 2 && (
                      <div className="w-full bg-yellow-400 bg-opacity-10 px-4 py-3 border border-yellow-500 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-yellow-600">
                            {pageData.unpaidStaustext}
                          </p>
                        </div>
                      </div>
                    )}

                    {invoiceData?.status === 1 && (
                      <div className="w-full bg-green-400 bg-opacity-10 px-4 py-3 border border-green-500 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-green-600">
                            {pageData.paidStatusText}
                          </p>
                        </div>
                      </div>
                    )}

                    {invoiceData?.status !== 1 && (
                      <div className="mt-5">
                        <Button
                          onClick={() =>
                            setConfirmationDialog(!confirmationDialog)
                          }
                          className="bg-shoorah-primary disabled:bg-[#F4F4F4] disabled:border-[#EAEAEA] border border-shoorah-primary rounded-full text-sm font-medium text-white disabled:text-black/70 px-5 py-2"
                        >
                          {pageData.updateStatusBtnText}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* THIRD SECTION */}
        <div className="mt-5">
          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
            <div className="p-5">
              {/* HEADING */}
              <div className="p-0">
                <h5 className="text-base font-medium text-black dark:text-white">
                  {pageData.confirmCommunicationLabel}
                </h5>
                <p className="text-sm font-normal text-black/80 dark:text-white/80">
                  {pageData.confirmCommunicationDescription}
                </p>
              </div>

              {/* CONTENT */}
              <div className="mt-5 flex items-center gap-2">
                <CustomCheckBox
                  check={emailCheckbox}
                  onClick={() => {
                    setEmailCheckbox(!emailCheckbox);
                    handleChangeEmailStatus();
                  }}
                  title={pageData.markedAsEmailedCompanyText}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONFIRMATION DIALOG */}
      <Dialog
        open={confirmationDialog}
        onClose={() => setConfirmationDialog(!confirmationDialog)}
        className="relative z-50"
      >
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4 bg-black bg-opacity-50">
          <DialogPanel className="max-w-lg space-y-4 border bg-white rounded-xl shadow-lg p-8">
            <div className="pb-5 border-b border-[#E7E8EA]">
              <DialogTitle className="font-bold text-lg">
                {pageData.updateStatusDialogTitle}
              </DialogTitle>
              <p>{pageData.updateStatusConfirmMessage}</p>
            </div>
            <div className="flex items-center justify-end gap-5">
              <button
                onClick={() => setConfirmationDialog(!confirmationDialog)}
              >
                {pageData.cancelBtnText}
              </button>
              <button
                onClick={handleChangeStatus}
                className="bg-shoorah-primary disabled:bg-[#F4F4F4] disabled:border-[#EAEAEA] border border-shoorah-primary rounded-full text-sm font-medium text-white disabled:text-black/70 px-5 py-2"
              >
                {pageData.markAsPaidBtnText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
};

export default GenerateInvoice;
